// src/components/settings/ProjectDetails.jsx
import React from 'react';
import { 
  X, 
  Edit, 
  FolderOpen,
  MapPin,
  Calendar,
  Users,
  Building,
  Phone,
  Mail,
  User,
  Target,
  DollarSign,
  Clock
} from 'lucide-react';

const ProjectDetails = ({ project, onClose, onEdit }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900">Project Details</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
          {/* Project Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-2xl font-bold text-gray-900">{project.name}</h2>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                {project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
              </span>
            </div>
            {project.description && (
              <p className="text-gray-600 leading-relaxed">{project.description}</p>
            )}
          </div>

          {/* Main Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Location Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Province:</span>
                    <span className="font-medium text-gray-900">{project.province}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">District:</span>
                    <span className="font-medium text-gray-900">{project.district}</span>
                  </div>
                  {project.circuit && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Circuit:</span>
                      <span className="font-medium text-gray-900">{project.circuit}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Timeline
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Date:</span>
                    <span className="font-medium text-gray-900">{formatDate(project.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">End Date:</span>
                    <span className="font-medium text-gray-900">{formatDate(project.endDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium text-gray-900">{formatDate(project.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="font-medium text-gray-900">{formatDate(project.updatedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Budget Information */}
              {project.budget && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Budget
                  </h4>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(project.budget)}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact Person:</span>
                    <span className="font-medium text-gray-900">{project.contactPerson || 'Not set'}</span>
                  </div>
                  {project.contactEmail && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email:</span>
                      <a 
                        href={`mailto:${project.contactEmail}`}
                        className="font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Mail className="w-3 h-3" />
                        {project.contactEmail}
                      </a>
                    </div>
                  )}
                  {project.contactPhone && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Phone:</span>
                      <a 
                        href={`tel:${project.contactPhone}`}
                        className="font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Phone className="w-3 h-3" />
                        {project.contactPhone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Participants */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Participants ({project.participants?.length || 0})
                </h4>
                <div className="text-sm text-gray-600">
                  {project.participants?.length > 0 ? (
                    <div className="space-y-2">
                      {project.participants.slice(0, 5).map((participant, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 text-xs font-medium">
                              {participant.firstName?.[0] || 'U'}
                            </span>
                          </div>
                          <span>{participant.firstName} {participant.lastName}</span>
                        </div>
                      ))}
                      {project.participants.length > 5 && (
                        <div className="text-gray-500 text-xs">
                          +{project.participants.length - 5} more participants
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>No participants added yet</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Project Objectives */}
          {project.objectives && project.objectives.length > 0 && (
            <div className="mt-8">
              <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Project Objectives
              </h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="space-y-2">
                  {project.objectives.filter(obj => obj.trim()).map((objective, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;